import React, { useState } from 'react';
import styled from 'styled-components';
import { DashboardCard as DashboardCardType } from '../types';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks } from '../constants/theme';

interface InteractiveDashboardCardProps {
  card: DashboardCardType;
  onPress?: () => void;
  onDrillDown?: (metric: string) => void;
  onExport?: (format: 'pdf' | 'excel' | 'csv') => void;
  onShare?: () => void;
  showActions?: boolean;
}

const CardContainer = styled.div`
  position: relative;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  cursor: pointer;
  transition: all 200ms ease;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-4px);
    
    .card-actions {
      opacity: 1;
      visibility: visible;
    }
  }

  &:focus-within {
    outline: none;
    box-shadow: 0 0 0 3px ${Colors.borderFocus}20;
  }
`;

const CardHeader = styled.div`
  padding: ${Spacing.xl}px ${Spacing.xl}px ${Spacing.lg}px;
  background: ${Colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CardTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
  font-weight: ${FontWeights.medium};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.3;
`;

const CardActions = styled.div`
  display: flex;
  gap: ${Spacing.xs}px;
  opacity: 0;
  visibility: hidden;
  transition: all 200ms ease;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? Colors.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.textSecondary};
  border: 1px solid ${props => props.variant === 'primary' ? Colors.primary : Colors.border};
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  cursor: pointer;
  transition: all 200ms ease;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? Colors.primaryDark : Colors.surfaceHover};
    border-color: ${props => props.variant === 'primary' ? Colors.primaryDark : Colors.borderDark};
  }
  
  &:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 1px;
  }
`;

const CardContent = styled.div`
  flex: 1;
  padding: ${Spacing.lg}px ${Spacing.xl}px ${Spacing.xl}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: ${Spacing.sm}px;
`;

const CardValue = styled.div`
  font-family: ${FontStacks.primary};
  font-size: 40px;
  font-weight: ${FontWeights.bold};
  line-height: 0.9;
  color: ${Colors.textPrimary};
  letter-spacing: -0.02em;
  transition: all 200ms ease;
  cursor: pointer;
  
  &:hover {
    color: ${Colors.primary};
  }
  
  &:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const TrendIndicator = styled.div<{ changeType: 'positive' | 'negative' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.bold};
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: 6px;
  border: 1px solid ${props => 
    props.changeType === 'positive' ? 'rgba(21, 128, 61, 0.2)' : 
    props.changeType === 'negative' ? 'rgba(153, 27, 27, 0.2)' : 'rgba(51, 65, 85, 0.15)'
  };
  background: ${props => 
    props.changeType === 'positive' ? 'rgba(21, 128, 61, 0.1)' : 
    props.changeType === 'negative' ? 'rgba(153, 27, 27, 0.1)' : 'rgba(51, 65, 85, 0.08)'
  };
  color: ${props => 
    props.changeType === 'positive' ? Colors.successDark : 
    props.changeType === 'negative' ? Colors.dangerDark : Colors.textPrimary
  };
  min-width: 60px;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => 
      props.changeType === 'positive' ? 'rgba(21, 128, 61, 0.3)' : 
      props.changeType === 'negative' ? 'rgba(153, 27, 27, 0.3)' : 'rgba(51, 65, 85, 0.2)'
    };
    background: ${props => 
      props.changeType === 'positive' ? 'rgba(21, 128, 61, 0.15)' : 
      props.changeType === 'negative' ? 'rgba(153, 27, 27, 0.15)' : 'rgba(51, 65, 85, 0.1)'
    };
  }
`;

const CardDescription = styled.p`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin: 0;
  line-height: 1.3;
`;

const DataBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, ${Colors.primary} 0%, ${Colors.accent} 100%);
  opacity: 0.8;
  border-radius: 0 0 ${BorderRadius.lg}px ${BorderRadius.lg}px;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  box-shadow: ${Shadows.lg};
  min-width: 120px;
  z-index: 10;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 200ms ease;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: none;
  border: none;
  text-align: left;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
  
  &:focus {
    outline: none;
    background: ${Colors.surfaceSelected};
    color: ${Colors.primary};
  }
  
  &:first-child {
    border-radius: ${BorderRadius.md}px ${BorderRadius.md}px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${BorderRadius.md}px ${BorderRadius.md}px;
  }
`;

const InteractiveDashboardCard: React.FC<InteractiveDashboardCardProps> = ({ 
  card, 
  onPress, 
  onDrillDown,
  onExport,
  onShare,
  showActions = true 
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);

  const getTrendIcon = (changeType: string) => {
    switch (changeType) {
      case 'positive': return '↑';
      case 'negative': return '↓';
      default: return '→';
    }
  };

  const formatValue = (value: string | number) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  const handleDrillDown = () => {
    onDrillDown?.(card.title);
  };

  const handleExport = (format: 'pdf' | 'excel' | 'csv') => {
    onExport?.(format);
    setShowExportMenu(false);
  };

  return (
    <CardContainer 
      className="interactive-card"
      onClick={onPress}
      role="button"
      tabIndex={0}
      aria-label={`${card.title}: ${formatValue(card.value)}. ${card.description || ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress?.();
        }
      }}
    >
      <CardHeader>
        <CardTitle>{card.title}</CardTitle>
        {showActions && (
          <CardActions className="card-actions">
            {onDrillDown && (
              <ActionButton
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDrillDown();
                }}
                aria-label={`Drill down into ${card.title} details`}
              >
                Drill Down
              </ActionButton>
            )}
            
            {onShare && (
              <ActionButton
                onClick={(e) => {
                  e.stopPropagation();
                  onShare();
                }}
                aria-label={`Share ${card.title}`}
              >
                Share
              </ActionButton>
            )}
            
            {onExport && (
              <div style={{ position: 'relative' }}>
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowExportMenu(!showExportMenu);
                  }}
                  aria-label="Export options"
                  aria-expanded={showExportMenu}
                  aria-haspopup="true"
                >
                  Export
                </ActionButton>
                
                <DropdownMenu isOpen={showExportMenu} role="menu">
                  <DropdownItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExport('pdf');
                    }}
                    role="menuitem"
                  >
                    Export as PDF
                  </DropdownItem>
                  <DropdownItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExport('excel');
                    }}
                    role="menuitem"
                  >
                    Export as Excel
                  </DropdownItem>
                  <DropdownItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExport('csv');
                    }}
                    role="menuitem"
                  >
                    Export as CSV
                  </DropdownItem>
                </DropdownMenu>
              </div>
            )}
          </CardActions>
        )}
      </CardHeader>
      
      <CardContent>
        <ValueContainer>
          <CardValue 
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleDrillDown();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleDrillDown();
              }
            }}
            aria-label={`${card.title} value: ${formatValue(card.value)}. Click to drill down`}
            role="button"
          >
            {formatValue(card.value)}
          </CardValue>
          
          {card.change !== undefined && (
            <TrendIndicator changeType={card.changeType || 'neutral'}>
              <span aria-hidden="true">{getTrendIcon(card.changeType || 'neutral')}</span>
              <span>{Math.abs(card.change)}%</span>
              <span className="sr-only">
                {card.changeType === 'positive' ? 'increased' : 
                 card.changeType === 'negative' ? 'decreased' : 'changed'} by {Math.abs(card.change)} percent
              </span>
            </TrendIndicator>
          )}
        </ValueContainer>
        
        {card.description && (
          <CardDescription>{card.description}</CardDescription>
        )}
        
        <DataBar aria-hidden="true" />
      </CardContent>
    </CardContainer>
  );
};

export default InteractiveDashboardCard;